<template>
<div
  :class="['recipes', { 'screen-lg': this.onTabletUp && this.currentPrimaryView.component === 'Recipes' }, this.currentView.context]"
  @scroll="scrollHandler"
>
  <DishFilters
    :onTabletUp="this.onTabletUp"
    :location="'desktop'"
    v-if="this.onTabletUp"
    @filter-dishes="onFilterDishes"
    @retry-filter-dishes="onRetryFilterDishes"
    @show-favorites="onShowFavorites"
    @hide-favorites="onHideFavorites"
  />
  <div class="search-wrapper" v-if="!this.el.is.sm">
    <span class="icon-clear-wrapper" @click="clearSearch" v-if="this.query !== ''">
      <BaseIcon class="icon-clear" name="times-circle" />
    </span>
    <baseInput
      inputType="text"
      :placeholder="$t('recipes.filters.searchPlaceholder')"
      v-model="query"
      v-if="!this.el.is.tabletUp"
      class="baseInputStyle"
      @keyup="filterDishes($event.target.value.toLowerCase())"
      @keyup.delete="onRetryFilterDishes($event.target.value.toLowerCase())"
    />
  </div>
  <div class="recipes-header" v-if="!this.el.is.tabletUp">
    <span class="filter-wrapper" @click="setToggleFilters">
      <transition name="fade" mode="out-in">
        <span key="noFilters" v-if="filterCount === 0">{{ $t('recipes.filters.lblFilterRecipes') }}</span>
        <span key="activeFilters" v-if="filterCount > 0">{{ `Using ${filterCount} filter${filterCount > 1 ? 's' : ''}` }}</span>
      </transition>
      <BaseIcon name="chevron-right" />
    </span>
    <span
      class="clear-filter-wrapper"
      @click="
        () => {
          setDishes([]);
          initializeFilterQueryString('reset');
          getDishes();
        }
      "
    >
      <transition name="fade">
        <span key="noFilters" v-if="filterCount === 0"></span>
        <span key="activeFilters" v-if="filterCount > 0">{{ $t('lblClearFilters') }}</span>
      </transition>
    </span>
  </div>

  <div :class="['recipe-items-wrapper', { 'filters-in-modal': this.currentPrimaryView }]" @scroll="scrollHandler">
    <div class="section-title">
      <h3 v-if="this.currentPrimaryView.component === 'Recipes'">
        {{ $t('recipes.title') }}
        <small>{{ $t('recipes.sutitle') }}</small>
      </h3>
    </div>
    <PlanLeftoversSlot
      :appliedMealtime="data.mealTime"
      v-if="data && data.leftoverOptionsCount > 0"
      @showLeftoverOptions="$emit('showLeftoverOptions', $event)"
      @showLeftoverDetails="$emit('showLeftoverDetails', $event)"
    />

    <MealCard
      v-for="(dish, i) in dishes"
      v-if="i < displayLimit && !isFavoritesVisible && dish.type !== 'm'"
      :key="i"
      :dish="dish"
      :loadingRecipe="loading"
      :cardType="'meal'"
      :onTabletUp="onTabletUp"
      :navigateHigherWhenDone="data && data.navigateHigherWhenDone ? data.navigateHigherWhenDone : false"
      @showDishDetails="showDishDetails"
    />

    <MealCard
      v-for="(dish, i) in favorites"
      v-if="i < displayLimit && isFavoritesVisible && dish.type !== 'm'"
      :key="i"
      :dish="dish"
      :loadingRecipe="loading"
      :cardType="'meal'"
      :onTabletUp="onTabletUp"
      :navigateHigherWhenDone="data && data.navigateHigherWhenDone ? data.navigateHigherWhenDone : false"
      @showDishDetails="showDishDetails"
    />

    <div v-if="!dishes.length || (showFavorites && !favorites.length)" class="empty-dishes">
      <h4>{{ $t('recipes.lblNoContent') }}</h4>
    </div>
  </div>
</div>
</template>

<script>
import MealCard from 'MealCard/MealCard';
import PlanLeftoversSlot from 'Plans/PlanLeftoversSlot';
import DishFilters from 'DishFilters/DishFilters';
import { breakpointsMixin } from 'breakpointsMixin';
import { gtmMixin } from 'gtmMixin';
import { shareMixin } from 'shareMixin';
import utils from '@src/utils/utils';
import {
  authComputed,
  dishesComputed,
  dishesMethods,
  favoritesComputed,
  kitchenDrawerComputed,
  kitchenDrawerMethods,
  mealsComputed,
  mealsMethods,
  primaryNavComputed,
  recipesComputed,
  recipesMethods,
  userComputed,
  userMethods,
  shareMethods,
  onboardingComputed,
} from '@state/helpers';

export default {
  mixins: [breakpointsMixin, gtmMixin, shareMixin],
  components: {
    DishFilters,
    MealCard,
    PlanLeftoversSlot,
  },
  props: {
    isEmbed: {
      type: Boolean,
    },
    deepLink: {
      type: String,
    },
    data: {
      type: Object,
    },
    onTabletUp: {
      type: Boolean,
    },
  },
  data() {
    return {
      initialDisplayLimit: 20,
      displayLimit: 0,
      maxScrollTop: 0,
      query: '',
      showFavorites: false,
      loading: null,
    };
  },
  computed: {
    ...authComputed,
    ...dishesComputed,
    ...favoritesComputed,
    ...kitchenDrawerComputed,
    ...mealsComputed,
    ...onboardingComputed,
    ...recipesComputed,
    ...primaryNavComputed,
    ...userComputed,
    currentView() {
      return this.navContent[this.activeNavIndex];
    },
    currentPrimaryView() {
      return this.primaryNavContent[this.activePrimaryNavIndex];
    },
  },
  mounted() {
    if (!this.isOnboardingEnabled) {
      this.initializeFilterQueryString('reset');
    }

    this.gtmTrackEvent('pageView', { action: 'Visit All Recipes', label: 'All Recipes View' });
    this.displayLimit = this.initialDisplayLimit;

    if (this.deepLink) {
      const deepLinkParts = utils.splitWithRemainder(this.deepLink, '/', 2);
      if (deepLinkParts && deepLinkParts.length > 0) {
        const dishId = parseInt(deepLinkParts[1].toLowerCase());

        if (deepLinkParts[0].toLowerCase() === 'r') {
          this.navigateIntoRecipe(dishId);
        } else if (deepLinkParts[0].toLowerCase() === 'm') {
          this.navigateIntoMeal(dishId);
        }
      }
    }
  },
  beforeDestroy() {
    if (this.loggedIn) {
      this.getDishes();
    }
  },
  methods: {
    ...dishesMethods,
    ...kitchenDrawerMethods,
    ...mealsMethods,
    ...recipesMethods,
    ...userMethods,
    ...shareMethods,
    scrollHandler() {
      let scrollContainer = null;
      let recipesPerRow = 1;

      if (this.onTabletUp && this.currentPrimaryView.component === 'Recipes') {
        scrollContainer = this.$el.getElementsByClassName('recipe-items-wrapper')[0];
        recipesPerRow = scrollContainer.offsetWidth <= 770 ? 2 : 3;
      } else {
        scrollContainer = this.$el;
      }

      const scrollTop = scrollContainer.scrollTop;

      if (this.maxScrollTop < scrollTop) {
        this.maxScrollTop = scrollTop;
      }

      const additionalCards = Math.floor((this.maxScrollTop * recipesPerRow) / 200);
      this.displayLimit = this.initialDisplayLimit + additionalCards;
    },
    async navigateIntoRecipe(dishId) {
      let recipeData = null;

      if (this.loggedIn) {
        if (!this.cachedRecipesMap[dishId]) {
          await this.getRecipe(dishId);
        }
      } else {
        recipeData = await this.getPublicRecipe(dishId);
      }

      const component = this.loggedIn ? 'RecipeDetail' : 'RecipeDetailPublic';
      const title = this.$t('lblRecipe');
      this.navigateDeeper({
        title: title,
        component: component,
        data: this.cachedRecipesMap[dishId] || recipeData,
        context: this.onTabletUp ? 'drawerModal' : null,
      });

      if (!this.currentView.deepLink) {
        if (this.onTabletUp && this.currentView.context !== 'drawerModal') {
          if (this.loggedIn && this.currentPrimaryView.component === 'Recipes') {
            this.toggleDrawer();
          } else {
            this.toggleDrawer({ desiredState: 'open' });
          }
        }
      }

      this.updateShareableUrl(component, recipeData || this.cachedRecipesMap[dishId]);
    },
    async navigateIntoMeal(dishId) {
      if (!this.cachedMeal[dishId]) {
        await this.getMeal(dishId);
      }

      this.navigateDeeper({
        title: 'Meal',
        component: 'MealDetail',
        data: this.cachedMeal[dishId],
        context: this.onTabletUp ? 'drawerModal' : null,
      });

      if (!this.currentView.deepLink) {
        if (this.onTabletUp && this.currentView.context !== 'drawerModal') {
          this.toggleDrawer({ desiredState: 'open' });
        }
      }

      this.updateShareableUrl('RecipeDetail', this.cachedMeal[dishId]);
    },
    async showDishDetails(dish) {
      this.loading = dish.id;
      if (dish.type === 'r') {
        await this.navigateIntoRecipe(dish.id);
      } else if (dish.type === 'm') {
        await this.navigateIntoMeal(dish.id);
      }
      this.loading = null;
    },
    updateDishResults(val) {
      this.setDishes(
        this.dishes.filter((dish) => {
          if (dish.name && dish.name.toLowerCase().includes(val)) {
            return dish;
          }
        })
      );
    },
    clearSearch() {
      if (this.query !== '') {
        this.query = '';
        this.getDishes();
      }
    },
    filterDishes(val) {
      if (val.length >= 2) {
        this.updateDishResults(val.toLowerCase());
      }
    },
    onFilterDishes(val) {
      this.updateDishResults(val);
    },
    onShowFavorites() {
      this.showFavorites = !this.showFavorites;
    },
    onHideFavorites() {
      this.showFavorites = !this.showFavorites;
    },
    async onRetryFilterDishes(val) {
      if (val === '') {
        this.getDishes();
      } else if (!this.dishes.length) {
        await this.getDishes();
        this.updateDishResults(val);
      } else {
        this.updateDishResults(val);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'theme.scss';

.baseInputStyle{
  color: black
}
.recipes {
  @extend %scrollable-drawer-content;

  padding: 0.6rem 0.6rem 11rem;

  .search-wrapper {
    align-items: center;
    display: flex;
    flex-direction: row-reverse;
    padding-bottom: 0.8rem;

    .icon-clear-wrapper {
      padding-right: 1.6rem;
      position: absolute;

      .icon-clear {
        font-size: 1.3rem;

        &:hover {
          cursor: pointer;
        }
      }
    }

    input {
      margin-bottom: 0;

      &::-webkit-input-placeholder {
        line-height: 1.4;
      }

      &::-moz-placeholder {
        line-height: 1.4;
      }

      &:-ms-input-placeholder {
        line-height: 1.4;
      }

      &:-moz-placeholder {
        line-height: 1.4;
      }
    }
  }

  .recipes-header {
    display: flex;
    font-size: 1.7rem;
    justify-content: space-between;
    margin-top: 1rem;
    padding: 0 1rem 2rem;

    span {
      margin-right: 1rem;
    }

    .fade-enter-active {
      transition: opacity 0.3s;
    }
    .fade-leave-active {
      transition: opacity 0.3s;
    }
    .fade-enter,
    .fade-leave-to {
      opacity: 0;
    }

    .filter-wrapper {
      &:hover {
        cursor: pointer;
      }
    }

    .clear-filter-wrapper {
      margin: 0.3rem 0 0;
      span {
        color: $color-action;
        margin-right: 0;
      }
    }
  }

  .empty-dishes {
    background-color: $color-empty-mealtime-slot-bg;
    border: 1px solid $base-border-color;
    border-radius: $base-border-radius;
    margin: 1rem 1rem;
    padding: 1.7rem;
    text-align: center;

    h4 {
      font-size: 1.6rem;
      margin-bottom: 0;
    }
  }

  &.screen-lg {
    display: flex;
    position: relative;

    .recipe-items-wrapper {
      align-content: start;
      align-items: auto;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      height: 107vh;
      justify-content: start;
      overflow-y: scroll;
      padding-bottom: 20rem;
      width: 100%;
    }

    .section-title {
      border-top: 4px solid $color-navbar-bg;
      margin-top: 2rem;
      padding: 1rem 0;
      width: 75%;

      h3 {
        line-height: 1.3;
        small {
          display: block;
        }
      }
    }

    .empty-dishes {
      width: 100%;
    }
  }

  &.drawerModal {
    background-color: $color-kitchen-drawer-bg-open;
    border-bottom-left-radius: 2.5rem;
    border-bottom-right-radius: 2.5rem;
    box-shadow: 0 3px 5px 0 rgba(0, 0, 0, 0.3);
  }
}
</style>
